/**
 * The "New session" form's rules (TRA-1327), kept out of the dialog so they
 * are testable without a DOM. The api checks the same limits.
 */

import { z } from 'zod'

import type { TrainingAgentSessionCreate } from '@/api/generated/model'

/** schemas/training_agent.py: TrainingAgentSessionCreate and MESSAGE_MAX_CHARS. */
const TITLE_MAX_CHARS = 200
export const MESSAGE_MAX_CHARS = 20_000

const newSessionSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, 'Give the session a title.')
    .max(TITLE_MAX_CHARS, `A title holds up to ${String(TITLE_MAX_CHARS)} characters.`),
  message: z
    .string()
    .trim()
    .max(
      MESSAGE_MAX_CHARS,
      `A first message holds up to ${MESSAGE_MAX_CHARS.toLocaleString('en-US')} characters.`,
    ),
})

export type NewSessionField = 'title' | 'message'

export interface NewSessionDraft {
  title: string
  message: string
}

export type NewSessionErrors = Partial<Record<NewSessionField, string>>

export type ParsedNewSession =
  | { kind: 'ok'; request: TrainingAgentSessionCreate }
  | { kind: 'error'; errors: NewSessionErrors }

/** The request to send, or each field's first problem. An empty message is left out. */
export function parseNewSession(draft: NewSessionDraft): ParsedNewSession {
  const result = newSessionSchema.safeParse(draft)
  if (result.success) {
    const { title, message } = result.data
    return { kind: 'ok', request: { title, message: message === '' ? null : message } }
  }
  const errors: NewSessionErrors = {}
  for (const issue of result.error.issues) {
    const field = issue.path[0]
    if ((field === 'title' || field === 'message') && !(field in errors)) {
      errors[field] = issue.message
    }
  }
  return { kind: 'error', errors }
}
